import classNames from 'classnames';
import * as _ from 'lodash';
import type { FunctionComponent, MouseEvent } from 'react';

import type { AsyncStatus, IAsyncStatus } from '../../types';

type ErrorAlertProps = {
  status: Omit<AsyncStatus, 'loading' | 'isLoading'> | IAsyncStatus | (Record<string, unknown> & { errors: Error[] });
  actionButtonText?: string;
  withMargin?: boolean;
  testId?: string;
  onAction?: (event: MouseEvent<HTMLElement>) => void;
};

/**
 * An ErrorAlert element renders the errors held in the status object, or null if there are no errors.
 *
 * @param {ErrorAlertProps} props - ErrorAlert props
 * @param {Partial<IAsyncStatus<string | Error>>} props.status - Async status (usually from redux).
 * @param {string} [props.actionButtonText] - The text of a button to show in the alert. If omitted, no button is shown.
 * @param {boolean} [props.withMargin] - If the "margins" class should be applied to the alert.
 * @param {string} [props.testId] - Adds a `data-test-id` attribute, useful for selenium testing.
 * @param {(event: MouseEvent<HTMLElement>) => void} [props.onAction] - The click handler for the action button.
 */
export const ErrorAlert: FunctionComponent<ErrorAlertProps> = ({
  status,
  actionButtonText,
  withMargin,
  testId,
  onAction,
}) => {
  let messages: string[] = [];
  if (status.errors && status?.errors?.length > 0) {
    messages = status.errors.map(err => err.message || err);
  } else if (status.error) {
    messages.push(status.error.message || status.error);
  }
  return messages.length > 0 && (
    <div
      className={classNames('alert', 'alert-error', {
        margins: withMargin,
        'with-action': !!actionButtonText,
      })}
      data-test-id={testId}
    >
      <span>{messages.join('\n\n')}</span>
      {!!actionButtonText && (
        <button
          type="button"
          className="unstyled"
          onClick={onAction || _.noop}
        >
          {actionButtonText}
        </button>
      )}
    </div>
  );
};
